import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Switch,
  Grid,
  TextField,
  Button,
  Divider,
  InputAdornment,
  Paper,
  Chip,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SyncIcon from '@mui/icons-material/Sync';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { BreadCrumbsAndTitle } from '../../../components/generics/BreadCrumbsAndTitle';
import { AlertsInformativos } from '../../../components/generics/AlertsInformativos';
import { SnackAlert } from '../../../components/SnackAlert';

import {
  getShiftSchedulingGlobalRulesStore,
  postShiftSchedulingGlobalRulesStore,
  putShiftSchedulingGlobalRulesStore,
} from '../../../store/shift_scheduling_global_rules/thunks';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';

export const ReglasGlobales = () => {
  const dispatch = useDispatch<any>();
  const { shiftSchedulingGlobalRule } = useSelector(
    (state: any) => state.shiftSchedulingGlobalRules
  );

  // State for base rules
  const [rules, setRules] = useState({
    maxHorasSemanales: 42,
    horasParaDescanso: 42,
    permitirAcumularDescansos: true,
    permitirTurnosRotativos: true,
    habilitarMedioTiempo: true,
    habilitarTurnosConcentrados: true,
  });

  const [alert_snack, setAlertSnack] = useState(false);
  const [msgSnack, setMsgSnack] = useState({
    msg: '',
    status: 'success',
  });
  const [timeAlert, setTimeAlert] = useState(3000);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    dispatch(getShiftSchedulingGlobalRulesStore());
  }, [dispatch]);

  useEffect(() => {
    if (shiftSchedulingGlobalRule) {
      setRules({
        maxHorasSemanales: shiftSchedulingGlobalRule.maxHorasSemanales ?? 42,
        horasParaDescanso: shiftSchedulingGlobalRule.horasParaDescanso ?? 42,
        permitirAcumularDescansos: shiftSchedulingGlobalRule.permitirAcumularDescansos === 1,
        permitirTurnosRotativos: shiftSchedulingGlobalRule.permitirTurnosRotativos === 1,
        habilitarMedioTiempo: shiftSchedulingGlobalRule.habilitarMedioTiempo === 1,
        habilitarTurnosConcentrados: shiftSchedulingGlobalRule.habilitarTurnosConcentrados === 1,
      });
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [shiftSchedulingGlobalRule]);

  const handleChange = (field: string, value: any) => {
    setRules((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    if (shiftSchedulingGlobalRule?.id && !isEditing) {
      setIsEditing(true);
      return;
    }

    const payload = {
      ...rules,
      permitirAcumularDescansos: rules.permitirAcumularDescansos ? 1 : 0,
      permitirTurnosRotativos: rules.permitirTurnosRotativos ? 1 : 0,
      habilitarMedioTiempo: rules.habilitarMedioTiempo ? 1 : 0,
      habilitarTurnosConcentrados: rules.habilitarTurnosConcentrados ? 1 : 0,
    };

    if (shiftSchedulingGlobalRule?.id) {
      dispatch(putShiftSchedulingGlobalRulesStore(shiftSchedulingGlobalRule.id, payload)).then(
        (res: any) => {
          if (res && res.ok) {
            setMsgSnack({ msg: 'Reglas globales actualizadas correctamente', status: 'success' });
            setAlertSnack(true);
            setIsEditing(false);
          } else {
            setMsgSnack({ msg: res?.message || 'Error al actualizar las reglas', status: 'error' });
            setAlertSnack(true);
          }
        }
      );
    } else {
      dispatch(postShiftSchedulingGlobalRulesStore(payload)).then((res: any) => {
        if (res && res.ok) {
          setMsgSnack({ msg: 'Reglas globales guardadas correctamente', status: 'success' });
          setAlertSnack(true);
          setIsEditing(false);
        } else {
          setMsgSnack({ msg: res?.message || 'Error al guardar las reglas', status: 'error' });
          setAlertSnack(true);
        }
      });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, margin: '0 auto', maxWidth: 1200 }}>
      <SnackAlert
        open={alert_snack}
        onclick={setAlertSnack}
        text={msgSnack.msg}
        severity={msgSnack.status}
      />
      {/* Header Section */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}
      >
        <Box>
          <BreadCrumbsAndTitle
            title="Reglas Globales de Programación"
            mainModule="Programador Inteligente"
            parent="Configuración Base"
            action=""
            icon="Settings"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <AlertsInformativos
              type="info"
              title="Información"
              content="Estas reglas aplican como plantilla base para la programación de turnos de todos los empleados de la empresa según la normatividad laboral vigente (Ej. Ley 2101 en Colombia - reducción jornada a 42h). Las reglas específicas por cargo o nocturnas se configuran aparte."
            />
          </Typography>
        </Box>
        <Button
          variant="contained"
          color={shiftSchedulingGlobalRule?.id && !isEditing ? 'success' : 'primary'}
          startIcon={
            shiftSchedulingGlobalRule?.id && !isEditing ? (
              <EditOutlinedIcon />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          onClick={handleSave}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow:
              shiftSchedulingGlobalRule?.id && !isEditing
                ? '0px 8px 16px rgba(76, 175, 80, 0.24)'
                : '0px 8px 16px rgba(105, 108, 255, 0.24)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow:
                shiftSchedulingGlobalRule?.id && !isEditing
                  ? '0px 12px 20px rgba(76, 175, 80, 0.35)'
                  : '0px 12px 20px rgba(105, 108, 255, 0.35)',
            },
          }}
        >
          {shiftSchedulingGlobalRule?.id && !isEditing
            ? 'Editar Configuración'
            : shiftSchedulingGlobalRule?.id
              ? 'Guardar Cambios'
              : 'Guardar Configuración'}
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Jornada y Descansos */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              p: 4,
              height: '100%',
              borderRadius: 4,
              boxShadow: '0 4px 24px 0 rgba(34, 41, 47, 0.05)',
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ color: 'primary.main', mr: 2, display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon />
              </Box>
              <Typography variant="h6" fontWeight="bold">
                Jornada Laboral y Descansos
              </Typography>
            </Box>
            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Máximo de horas consecutivas trabajadas (Semanal)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  disabled={!isEditing}
                  value={rules.maxHorasSemanales}
                  onChange={(e) => handleChange('maxHorasSemanales', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Horas</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Descanso entre turnos (Horas requeridas para 1 día de descanso)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  disabled={!isEditing}
                  value={rules.horasParaDescanso}
                  onChange={(e) => handleChange('horasParaDescanso', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Horas</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Flexibilidad y Acuerdos */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              p: 4,
              height: '100%',
              borderRadius: 4,
              boxShadow: '0 4px 24px 0 rgba(34, 41, 47, 0.05)',
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ color: 'secondary.main', mr: 2, display: 'flex', alignItems: 'center' }}>
                <HandshakeIcon />
              </Box>
              <Typography variant="h6" fontWeight="bold">
                Acuerdos y Flexibilidad
              </Typography>
            </Box>
            <Divider sx={{ mb: 4 }} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
                p: 2,
                bgcolor: 'rgba(0,0,0,0.02)',
                borderRadius: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  Acumular días de descanso
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Permite acumular días seguidos de descanso por mutuo acuerdo entre empleado y
                  empresa.
                </Typography>
              </Box>
              <Switch
                disabled={!isEditing}
                checked={rules.permitirAcumularDescansos}
                onChange={(e) => handleChange('permitirAcumularDescansos', e.target.checked)}
                color="primary"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                bgcolor: 'rgba(0,0,0,0.02)',
                borderRadius: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  Permitir Turnos Rotativos (Horarios Mixtos)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Autoriza al programador automático a rotar a un mismo empleado entre diferentes
                  franjas horarias (mañana, tarde o noche) en sus días de trabajo.
                </Typography>
              </Box>
              <Switch
                disabled={!isEditing}
                checked={rules.permitirTurnosRotativos}
                onChange={(e) => handleChange('permitirTurnosRotativos', e.target.checked)}
                color="secondary"
              />
            </Box>
          </Card>
        </Grid>

        {/* Tipos de Turnos Habilitados */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              p: 4,
              borderRadius: 4,
              boxShadow: '0 4px 24px 0 rgba(34, 41, 47, 0.05)',
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ color: 'success.main', mr: 2, display: 'flex', alignItems: 'center' }}>
                <EventAvailableIcon />
              </Box>
              <Typography variant="h6" fontWeight="bold">
                Modalidades de Turno Habilitadas
              </Typography>
            </Box>
            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: rules.habilitarMedioTiempo ? 'primary.main' : 'grey.200',
                    borderRadius: 3,
                    transition: 'all 0.2s',
                    bgcolor: rules.habilitarMedioTiempo ? 'primary.50' : 'transparent',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color={rules.habilitarMedioTiempo ? 'primary.main' : 'text.primary'}
                      >
                        Medio Tiempo
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Turnos de media jornada (Ej. 21 horas semanales).
                      </Typography>
                    </Box>
                    <Switch
                      disabled={!isEditing}
                      checked={rules.habilitarMedioTiempo}
                      onChange={(e) => handleChange('habilitarMedioTiempo', e.target.checked)}
                    />
                  </Box>
                  {rules.habilitarMedioTiempo && (
                    <Chip
                      label="Activo"
                      size="small"
                      color="primary"
                      sx={{ mt: 2, fontWeight: 'bold' }}
                    />
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: rules.habilitarTurnosConcentrados ? 'success.main' : 'grey.200',
                    borderRadius: 3,
                    transition: 'all 0.2s',
                    bgcolor: rules.habilitarTurnosConcentrados ? 'success.50' : 'transparent',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color={rules.habilitarTurnosConcentrados ? 'success.main' : 'text.primary'}
                      >
                        Jornada Concentrada
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Agrupar horas en menos días (Ej. 3 días de 8 horas semanales).
                      </Typography>
                    </Box>
                    <Switch
                      disabled={!isEditing}
                      color="success"
                      checked={rules.habilitarTurnosConcentrados}
                      onChange={(e) =>
                        handleChange('habilitarTurnosConcentrados', e.target.checked)
                      }
                    />
                  </Box>
                  {rules.habilitarTurnosConcentrados && (
                    <Chip
                      label="Activo"
                      size="small"
                      color="success"
                      sx={{ mt: 2, fontWeight: 'bold' }}
                    />
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
